
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { IntlProvider } from 'next-intl';
import { IndexContextProvider, IndexContextProps } from '@/stores/index';
import Layout from '@/components/layout';
import request from '@/utils/request';

import './global.scss';

const processMenu = (menu: any[], locale: string): any => {
  return menu.map((item: any) => ({
    ...item,
    _title: locale === "en" ? item?.name : item?.menu_name,
    _href: item?.outlink || (item?.childlist?.length > 0 ? item?.childlist[0]?.outlink || item?.childlist[0]?.pathinfo : item?.pathinfo),
    _target: item?.outlink ? item?.target : (item?.childlist?.length > 0 ? item?.childlist[0]?.target : item?.target),
    childlist: [...processMenu(item?.childlist, locale)]
  }));
};

const getMenuCurrent = (menu: any[], path: string): any => {
  for (let i = 0; i < menu?.length; i++) {
    const item = menu[i];
    if (item.pathinfo === path) {
      return item;
    }

    if (item.childlist && Array.isArray(item.childlist)) {
      const child = getMenuCurrent(item.childlist, path) as any;
      if (child) {
        return child;
      }
    }
  }

  return null;
}

const getMenuParent = (menu: any[], pid: string): any => {
  for (let i = 0; i < menu?.length; i++) {
    const item = menu[i];
    if (item.childlist && Array.isArray(item.childlist)) {
      if (item.id === pid) {
        return item;
      }
    }
  }

  return null;
}

function MyApp(data: AppProps & IndexContextProps & { locale: string; messages: any }): JSX.Element {
  const { Component, pageProps, router, locale, messages, config, menu, expo } = data;
  const menuCurrent = getMenuCurrent(menu, router.pathname);
  const menuParent = getMenuParent(menu, menuCurrent?.pid);
  // console.log("MyApp", menu, menuCurrent, menuParent);
  return (
    <div>
      <Head>
        <title>{config?.[`meta_title_${locale}`]}</title>
        <meta name="keywords" content={config?.[`meta_kws_${locale}`]} />
        <meta name="description" content={config?.[`meta_desc_${locale}`]} />
        <meta name="viewport" content="width=device-width,user-scalable=no,initial-scale=1,maximum-scale=1,minimum-scale=1" />
        <meta name='thumbnail' content='/image/logo.png' />
        <link rel="icon" type="image/png" href="/image/logo.png" />
        {/* Google 代码 */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-NHE0XK96BS"></script>
        <script
          defer
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-NHE0XK96BS');
            `
          }}
        />
        <script
          defer
          dangerouslySetInnerHTML={{
            __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-NSWKDFH5');
            `
          }}
        />
        <noscript
          dangerouslySetInnerHTML={{
            __html: `
              <iframe src="https://www.googletagmanager.com/ns.html?id=GTM-NSWKDFH5"
              height="0" width="0" style="display:none;visibility:hidden"></iframe>
            `
          }} />
        {/* 百度统计 */}
        <meta name="baidu-site-verification" content="codeva-z8bL0PSeuE" />
        <script
          defer
          dangerouslySetInnerHTML={{
            __html: `
              var _hmt = _hmt || [];
              (function() {
                var hm = document.createElement("script");
                hm.src = "https://hm.baidu.com/hm.js?d40a5673b09c8b71bd6aa9c0f8ecac75";
                var s = document.getElementsByTagName("script")[0]; 
                s.parentNode.insertBefore(hm, s);
              })();
            `
          }}
        />
        <script type="text/javascript" defer src="/swiper5/js/swiper.min.js"></script>
        <script type="text/javascript" defer src="/swiper5/js/jquery.min.js"></script>
        <script type="text/javascript" defer src="/swiper5/js/jquery.waypoints.min.js"></script>
        <script type="text/javascript" defer src="/swiper5/js/jquery.countup.min.js"></script>
      </Head>
      <IntlProvider locale={locale} messages={messages}>
        <IndexContextProvider value={{ config, expo, menu, menuCurrent, menuParent }}>
          <Layout>
            <Component {...pageProps} />
          </Layout>
        </IndexContextProvider>
      </IntlProvider>
    </div>
  )
}

MyApp.getInitialProps = async (context: any) => {
  const locale = context.ctx.locale;
  const { data: config } = await request.get(`/api/get_config`);
  const { data } = await request.get(`/api/menu`, {
    params: {
      lang: locale || 'cn'
    }
  })
  const messages = await import(`../locales/${locale}/index.json`);
  // console.log("getInitialProps", messages);
  return {
    locale,
    messages,
    config: config?.data || null,
    menu: processMenu(data?.data?.menu_tree, locale) || [],
    expo: data?.data?.expo_info || null,
  };
};

export default MyApp;
